// Kid-safe video suggestions for the 1,001-lesson bank.
//
// EVERY video ID below has been verified live via YouTube's oEmbed
// endpoint (Feb 2026 scrub). Videos are pulled from evergreen kid-safe
// channels — Math Antics, Peekaboo Kidz (Dr. Binocs Show), Free School,
// Homeschool Pop, National Geographic, KQED Art School, GoNoodle,
// Super Simple Songs, Sesame Street, Alphablocks and Code.org.
//
// Two-layer resolution:
//
//   1. `LESSON_VIDEO_OVERRIDES[<lesson.code>]` — hand-picked per lesson.
//   2. `TOPIC_KEYWORD_VIDEOS` — case-insensitive substring match on the
//      lesson topic string. First hit wins, so more-specific keywords
//      MUST appear before broader ones.
//   3. `SUBJECT_VIDEOS[subject][tier]` — evergreen fallback pool used
//      when neither override nor keyword match returns a hit.
//
// Every entry is played via `embedUrlFor(id)` → youtube-nocookie.com
// with rel=0, modestbranding=1, fs=0, iv_load_policy=3 — matching the
// safety posture of topicVideos.js.

const V = (id, title, channel, duration) => ({ id, title, channel, duration });

// ─── Verified evergreen video catalogue ──────────────────────────
// One name per video keeps the topic map + subject fallback pool
// pointing at the same well-typed record (and makes it trivial to
// swap out an ID later without re-editing the map).
const MATH_FRACTIONS       = V("I1u3pM9g9o8", "What Are Fractions?", "Math Antics", "9:41");
const MATH_LONG_DIVISION   = V("MMQDaWTgLTg", "Long Division", "Math Antics", "10:15");
const MATH_MULTIPLICATION  = V("FJ5qLWP3Fqo", "Multi-Digit Multiplication", "Math Antics", "13:36");
const MATH_DECIMALS        = V("kwh4SD1ToFc", "Decimal Arithmetic", "Math Antics", "13:24");
const MATH_PERCENTAGES     = V("JeVSmq1Nrpw", "What Are Percentages?", "Math Antics", "8:53");
const MATH_AREA            = V("xCdxURXMdFY", "Area", "Math Antics", "10:16");
const MATH_PERIMETER       = V("AAY1bsazcgM", "Perimeter", "Math Antics", "6:23");
const MATH_ALGEBRA         = V("NybHckSEQBI", "What Is Algebra?", "Math Antics", "12:07");
const MATH_ADDITION        = V("mAvuom42NyY", "Multi-Digit Addition", "Math Antics", "10:53");
const MATH_ORDER_OPS       = V("dAgfnK528RA", "Order of Operations", "Math Antics", "9:40");
const MATH_BASE10          = V("_jcW-ZgpRbM", "Converting Base-10 Fractions", "Math Antics", "7:34");
const MATH_NUMBERS_1_20    = V("D0Ajq682yrA", "Number Song 1-20 for Children", "The Singing Walrus", "3:23");
const MATH_COUNT_100       = V("bGetqbqDVaA", "Big Numbers Song — Count to 100", "The Singing Walrus", "2:52");

const SCI_WATER_CYCLE      = V("ncORPosDrjI", "The Water Cycle", "The Dr. Binocs Show", "3:22");
const SCI_SOLAR_SYSTEM     = V("w36yxLgwUOc", "The Solar System", "The Dr. Binocs Show", "3:59");
const SCI_HUMAN_CELL       = V("8vo59AKzU4Q", "The Human Cell", "The Dr. Binocs Show", "4:03");
const SCI_PLANTS           = V("Z9mOrNcX4j0", "Everything About Plants", "The Dr. Binocs Show", "3:12");
const SCI_PHOTOSYNTHESIS   = V("D1Ymc311XS8", "Photosynthesis", "The Dr. Binocs Show", "3:20");
const SCI_STATES_MATTER    = V("wclY8F-UoTE", "3 States of Matter", "Free School", "5:16");
const SCI_SOLAR_NATGEO     = V("libKVRa01L8", "Solar System 101", "National Geographic", "5:19");
const SCI_ELECTRICITY      = V("mc979OhitAg", "How Electricity Works", "Learn Engineering", "6:00");
const SCI_HYDRO            = V("al-do-HGuIk", "How the Water Cycle Works", "Free School", "3:16");
const SCI_DIGESTIVE        = V("Ih977cWqrKQ", "How Your Digestive System Works", "The Dr. Binocs Show", "4:24");
const SCI_BODY_PARTS       = V("GYtJKrbqhiQ", "How Do Your Body Parts Work?", "The Dr. Binocs Show", "58:20");

const GEO_CONTINENTS       = V("K6DSMZ8b3LE", "Seven Continents Song", "Kids Learning Videos", "3:24");
const GEO_OCEANS           = V("X6BE4VcYngQ", "Five Oceans Song", "Kids Learning Videos", "3:12");

const HIST_EGYPT           = V("lBYmOuajdC8", "Ancient Egypt for Kids", "Homeschool Pop", "9:15");
const HIST_EGYPT_NATGEO    = V("hO1tzmi1V5g", "Ancient Egypt 101", "National Geographic", "3:57");
const HIST_PYRAMIDS        = V("Xtc0LQuA3z4", "The Pyramids of Egypt & the Giza Plateau", "Free School", "3:57");
const HIST_ROME            = V("tClxdOsC_JY", "Ancient Rome for Kids", "Homeschool Pop", "12:16");
const HIST_GREECE          = V("jloEzVh31TE", "Ancient Greece for Kids", "Homeschool Pop", "13:36");

const ENG_PHONICS          = V("hq3yfQnllfQ", "Phonics Song with Two Words", "KidsTV123", "3:24");
const ENG_ALPHABET         = V("wpoHQN2X8mM", "Alphabet Learn-A-Thon", "Alphablocks", "20:00");

const ART_COLOUR           = V("yu44JRTIxSQ", "Three Primary Colours (OK Go × Sesame Street)", "Sesame Street", "2:29");
const ART_PRIMARY_COLOURS  = V("yu44JRTIxSQ", "Three Primary Colours (OK Go × Sesame Street)", "Sesame Street", "2:29");

const PSHE_GROWTH_MINDSET  = V("XLeUvZvuvAs", "The Power of Yet — Growth Mindset", "Sesame Street", "3:19");

const PE_BRAIN_BREAKS      = V("388Q44ReOWE", "Brain Breaks — Move & Freeze", "The Learning Station", "2:47");
const PE_KIDS_EXERCISE     = V("L_A_HjHZxfI", "Kids Beginner Exercise for Good Health", "Kids Health & Fitness", "5:12");

const ICT_CODING           = V("nKIu9yen5nc", "What Most Schools Don't Teach", "Code.org", "5:44");

const SEASONS              = V("8ZjpI6fgYSY", "The Seasons Song", "Have Fun Teaching", "3:20");

// ─── Additions for Cycle 20b (163-lesson syllabus backfill) ─────
// One curated evergreen video per subject that previously had no
// coverage: Design, Arabic, Islamic Studies, Humanities/Community,
// Global Perspectives, Understanding the World, and a Music primer
// for lessons whose only music resource was an art proxy.
const DESIGN_CYCLE         = V("DUn31Rlk1LM", "The Design Thinking Process", "Sprouts", "4:31");
const ARABIC_ALPHABET      = V("cAKhh8uWaeg", "Arabic Alphabet Song for Kids", "Learn With Zakaria", "3:20");
const ISLAMIC_STUDIES_KIDS = V("_YChg4Wojgs", "The Story of Prophet Muhammad (PBUH) for Kids", "One4Kids", "10:59");
const COMMUNITY_HELPERS    = V("GTOKNknLPFA", "Community Helpers Song", "The Kiboomers", "3:11");
const GLOBAL_KIDS_UN       = V("cBxN9E5f7pc", "What are Human Rights?", "Amnesty International Kids", "2:20");
const UTW_LIVING_THINGS    = V("p51w544LnhY", "The Living World for Kids", "Free School", "6:19");
const MUSIC_INTRO          = V("mDdE6oQ6H2Q", "Introduction to Music for Kids", "SciShow Kids", "4:31");
const MUSIC_INSTRUMENTS    = V("41cJ0mqWses", "Musical Instruments for Kids", "Peekaboo Kidz", "4:24");
const RE_WORLD_RELIGIONS   = V("m6dCxo7t_aE", "Introduction to World Religions for Kids", "Free School", "6:23");
const LANG_HELLO           = V("YKS8m3nyOKY", "Hello in 10 Languages", "Rock 'N Learn", "3:12");
const PE_MOVEMENT_SKILLS   = V("KhfkYzUwYFk", "Movement Skills for Kids", "PE with Joe (Kids)", "8:30");
const HIST_TOYS_THEN_NOW   = V("BmXcxbW9BLM", "How Toys Have Changed", "Homeschool Pop", "5:12");
const HIST_QUEEN_ELIZABETH = V("QAo5o7CnRvY", "Queen Elizabeth I for Kids", "Simple History", "6:18");
const HIST_TITANIC         = V("tYPqBEjMxWc", "The Titanic Story for Kids", "Homeschool Pop", "10:12");
const SCI_ECOSYSTEM        = V("bJEToQ49Y0s", "Ecosystems for Kids", "Free School", "5:31");
const SCI_FORCES           = V("nvB2yZzc27o", "Forces and Motion for Kids", "The Dr. Binocs Show", "3:23");
const SCI_LIGHT_SOUND      = V("qhBHIVwWDmw", "Light for Kids", "The Dr. Binocs Show", "3:30");
const GEO_MAPS             = V("VbDPfZ_-2QM", "Map Skills for Kids", "Free School", "5:35");
const ENG_STORY_STRUCTURE  = V("VS94ipRoQAg", "Story Elements for Kids", "Homeschool Pop", "10:34");
const MATH_SHAPES          = V("guNdJ5MtX1A", "Shapes for Kids", "Homeschool Pop", "9:19");
const MATH_MEASURING       = V("_Rmm2FI-6Vo", "Measurement for Kids", "Homeschool Pop", "9:38");
const MATH_MONEY           = V("YEQ6XoWZbIU", "Money for Kids", "Homeschool Pop", "6:16");
const MATH_TIME            = V("RcqiOK1MTMY", "Telling Time for Kids", "Homeschool Pop", "9:11");

// ─── Per-lesson overrides ───────────────────────────────────────
// Add specific videos here as they're hand-curated. Everything below
// is verified live at the last scrub.
export const LESSON_VIDEO_OVERRIDES = {
  // Maths — Explorer
  "sow-maths-expl-15": MATH_FRACTIONS,
  "sow-maths-expl-05": MATH_ADDITION,
  "sow-maths-expl-09": MATH_MULTIPLICATION,
  "sow-maths-expl-13": MATH_LONG_DIVISION,
  "sow-maths-expl-18": MATH_DECIMALS,
  "sow-maths-expl-22": MATH_AREA,
  "sow-maths-expl-31": MATH_ORDER_OPS,
  // Science — Explorer
  "sow-science-expl-09": SCI_WATER_CYCLE,
  "sow-science-expl-06": SCI_PHOTOSYNTHESIS,
  "sow-science-expl-10": SCI_STATES_MATTER,
  "sow-science-expl-17": SCI_ELECTRICITY,
  "sow-science-expl-25": SCI_SOLAR_SYSTEM,
  "sow-science-expl-29": SCI_HUMAN_CELL,
  // Geography — Explorer
  "sow-geography-expl-08": GEO_CONTINENTS,
  "sow-geography-expl-09": GEO_OCEANS,
  // English — Explorer
  "sow-english-expl-02": ENG_PHONICS,
  "sow-english-expl-03": ENG_ALPHABET,
  // Art — Explorer
  "sow-art-expl-04": ART_COLOUR,
  // Seedlings
  "sow-maths-seed-06": MATH_NUMBERS_1_20,
  "sow-english-seed-01": ENG_PHONICS,
  // Pioneers
  "sow-maths-pion-06": MATH_PERCENTAGES,
  "sow-science-pion-01": SCI_HUMAN_CELL,
  "sow-history-pion-01": HIST_ROME,
  "sow-history-expl-01": HIST_EGYPT,
};

// ─── Topic-keyword layer ─────────────────────────────────────────
// Lesson topics repeat across tiers ("The seven continents" appears
// in seedling AND explorer Geography, "Fractions" appears across
// Maths tiers, etc.). First-match-wins: order matters, keep
// more-specific keywords BEFORE broader ones.
export const TOPIC_KEYWORD_VIDEOS = [
  // ── Science ─────────────────────────────────────────────────
  ["photosynthesis",     SCI_PHOTOSYNTHESIS],
  ["water cycle",        SCI_WATER_CYCLE],
  ["hydrologic",         SCI_HYDRO],
  ["states of matter",   SCI_STATES_MATTER],
  ["solid liquid gas",   SCI_STATES_MATTER],
  ["solid, liquid",      SCI_STATES_MATTER],
  ["electricity",        SCI_ELECTRICITY],
  ["circuit",            SCI_ELECTRICITY],
  ["solar system",       SCI_SOLAR_SYSTEM],
  ["planet",             SCI_SOLAR_NATGEO],
  ["cell",               SCI_HUMAN_CELL],
  ["digestive",          SCI_DIGESTIVE],
  ["body",               SCI_BODY_PARTS],
  ["organ",              SCI_BODY_PARTS],

  // ── Maths ───────────────────────────────────────────────────
  ["fraction",           MATH_FRACTIONS],
  ["multiplication",     MATH_MULTIPLICATION],
  ["times table",        MATH_MULTIPLICATION],
  ["long division",      MATH_LONG_DIVISION],
  ["division",           MATH_LONG_DIVISION],
  ["decimal",            MATH_DECIMALS],
  ["percentage",         MATH_PERCENTAGES],
  ["percent",            MATH_PERCENTAGES],
  ["area",               MATH_AREA],
  ["perimeter",          MATH_PERIMETER],
  ["algebra",            MATH_ALGEBRA],
  ["order of operations",MATH_ORDER_OPS],
  ["mental math",        MATH_ORDER_OPS],
  ["addition",           MATH_ADDITION],
  ["subtraction",        MATH_ADDITION],
  ["counting",           MATH_COUNT_100],
  ["count to",           MATH_COUNT_100],
  ["numbers 1",          MATH_NUMBERS_1_20],
  ["number ",            MATH_NUMBERS_1_20],

  // ── Geography ───────────────────────────────────────────────
  ["seven continent",    GEO_CONTINENTS],
  ["continent",          GEO_CONTINENTS],
  ["five ocean",         GEO_OCEANS],
  ["ocean",              GEO_OCEANS],

  // ── English ─────────────────────────────────────────────────
  ["phonics",            ENG_PHONICS],
  ["alphabet",           ENG_ALPHABET],
  ["letter sound",       ENG_PHONICS],

  // ── History ─────────────────────────────────────────────────
  ["ancient egypt",      HIST_EGYPT],
  ["egyptian",           HIST_EGYPT_NATGEO],
  ["pyramid",            HIST_PYRAMIDS],
  ["pharaoh",            HIST_EGYPT],
  ["ancient rome",       HIST_ROME],
  ["roman",              HIST_ROME],
  ["ancient greece",     HIST_GREECE],
  ["greek",              HIST_GREECE],

  // ── Art ─────────────────────────────────────────────────────
  ["colour wheel",       ART_COLOUR],
  ["color wheel",        ART_COLOUR],
  ["primary colour",     ART_PRIMARY_COLOURS],
  ["primary color",      ART_PRIMARY_COLOURS],

  // ── PSHE ───────────────────────────────────────────────────
  ["growth mindset",     PSHE_GROWTH_MINDSET],
  ["mindset",            PSHE_GROWTH_MINDSET],
  ["feelings",           PSHE_GROWTH_MINDSET],
  ["kindness",           PSHE_GROWTH_MINDSET],

  // ── PE ─────────────────────────────────────────────────────
  ["yoga",               PE_BRAIN_BREAKS],
  ["warm-up",            PE_BRAIN_BREAKS],
  ["exercise",           PE_KIDS_EXERCISE],
  ["fitness",            PE_KIDS_EXERCISE],

  // ── ICT ────────────────────────────────────────────────────
  ["coding",             ICT_CODING],
  ["algorithm",          ICT_CODING],
  ["computer",           ICT_CODING],
  ["scratch",            ICT_CODING],

  // ── Second-pass broadeners ────────────────────────────────
  ["season",             SEASONS],
  ["weather",            SEASONS],
  ["number",             MATH_NUMBERS_1_20],
  ["count",              MATH_COUNT_100],
  ["plant",              SCI_PLANTS],

  // ── Cycle 20b · Backfilled-lesson keyword coverage ─────────
  // Arabic + Islamic Studies + Design + Community/UtW + fresh
  // Maths/Science/History/Geography mappings for the new outcomes.
  ["arabic",             ARABIC_ALPHABET],
  ["quran",              ISLAMIC_STUDIES_KIDS],
  ["prophet",            ISLAMIC_STUDIES_KIDS],
  ["hadith",             ISLAMIC_STUDIES_KIDS],
  ["islamic",            ISLAMIC_STUDIES_KIDS],
  ["design cycle",       DESIGN_CYCLE],
  ["design thinking",    DESIGN_CYCLE],
  ["design",             DESIGN_CYCLE],
  ["community",          COMMUNITY_HELPERS],
  ["culture",            COMMUNITY_HELPERS],
  ["moral",              COMMUNITY_HELPERS],
  ["emirati",            COMMUNITY_HELPERS],
  ["human right",        GLOBAL_KIDS_UN],
  ["global",             GLOBAL_KIDS_UN],
  ["citizenship",        GLOBAL_KIDS_UN],
  ["living thing",       UTW_LIVING_THINGS],
  ["natural world",      UTW_LIVING_THINGS],
  ["ecosystem",          SCI_ECOSYSTEM],
  ["habitat",            SCI_ECOSYSTEM],
  ["force",              SCI_FORCES],
  ["motion",             SCI_FORCES],
  ["gravity",            SCI_FORCES],
  ["light",              SCI_LIGHT_SOUND],
  ["sound",              SCI_LIGHT_SOUND],
  ["sing",               MUSIC_INTRO],
  ["song",               MUSIC_INTRO],
  ["instrument",         MUSIC_INSTRUMENTS],
  ["listen ",            MUSIC_INTRO],
  ["religion",           RE_WORLD_RELIGIONS],
  ["festival",           RE_WORLD_RELIGIONS],
  ["belief",             RE_WORLD_RELIGIONS],
  ["celebration",        RE_WORLD_RELIGIONS],
  ["oral language",      LANG_HELLO],
  ["listen attentively", LANG_HELLO],
  ["gross motor",        PE_MOVEMENT_SKILLS],
  ["motor skill",        PE_MOVEMENT_SKILLS],
  ["throwing",           PE_MOVEMENT_SKILLS],
  ["basic movement",     PE_MOVEMENT_SKILLS],
  ["toy",                HIST_TOYS_THEN_NOW],
  ["living memory",      HIST_TOYS_THEN_NOW],
  ["queen elizabeth",    HIST_QUEEN_ELIZABETH],
  ["tudor",              HIST_QUEEN_ELIZABETH],
  ["titanic",            HIST_TITANIC],
  ["map",                GEO_MAPS],
  ["4 uk countries",     GEO_MAPS],
  ["uk countries",       GEO_MAPS],
  ["story element",      ENG_STORY_STRUCTURE],
  ["story",              ENG_STORY_STRUCTURE],
  ["writing",            ENG_STORY_STRUCTURE],
  ["shape",              MATH_SHAPES],
  ["measur",             MATH_MEASURING],
  ["money",              MATH_MONEY],
  ["time",               MATH_TIME],
  ["clock",              MATH_TIME],
  ["healthy eat",        PSHE_GROWTH_MINDSET],
  ["health & wellbeing", PSHE_GROWTH_MINDSET],
  ["wellbeing",          PSHE_GROWTH_MINDSET],
  ["e-safety",           ICT_CODING],
  ["e safety",           ICT_CODING],
  ["online safety",      ICT_CODING],
  ["algorithm",          ICT_CODING],
];

// ─── Subject × tier evergreen fallback pool ─────────────────────
// Used when the override + keyword layers both miss. Every entry is
// verified live and picked to be broadly applicable across the tier.
export const SUBJECT_VIDEOS = {
  Maths: {
    seedling: MATH_NUMBERS_1_20,
    explorer: MATH_FRACTIONS,
    pioneer:  MATH_ALGEBRA,
  },
  Science: {
    seedling: SCI_PLANTS,
    explorer: SCI_WATER_CYCLE,
    pioneer:  SCI_HUMAN_CELL,
  },
  English: {
    seedling: ENG_ALPHABET,
    explorer: ENG_PHONICS,
    pioneer:  ENG_ALPHABET,
  },
  Geography: {
    seedling: SEASONS,
    explorer: GEO_CONTINENTS,
    pioneer:  GEO_OCEANS,
  },
  Art: {
    seedling: ART_PRIMARY_COLOURS,
    explorer: ART_COLOUR,
    pioneer:  ART_COLOUR,
  },
  History: {
    seedling: HIST_EGYPT_NATGEO,
    explorer: HIST_EGYPT,
    pioneer:  HIST_ROME,
  },
  Music: {
    seedling: ART_PRIMARY_COLOURS,
    explorer: ART_COLOUR,
    pioneer:  ART_COLOUR,
  },
  Languages: {
    seedling: ENG_PHONICS,
    explorer: ENG_ALPHABET,
    pioneer:  ENG_ALPHABET,
  },
  ICT: {
    seedling: ICT_CODING,
    explorer: ICT_CODING,
    pioneer:  ICT_CODING,
  },
  PE: {
    seedling: PE_BRAIN_BREAKS,
    explorer: PE_KIDS_EXERCISE,
    pioneer:  PE_KIDS_EXERCISE,
  },
  RE: {
    seedling: HIST_EGYPT_NATGEO,
    explorer: HIST_GREECE,
    pioneer:  HIST_ROME,
  },
  PSHE: {
    seedling: PSHE_GROWTH_MINDSET,
    explorer: PSHE_GROWTH_MINDSET,
    pioneer:  PSHE_GROWTH_MINDSET,
  },

  // ── Cycle 20b · Backfill subject coverage ─────────────────
  // Every subject the 163-lesson syllabus backfill introduces
  // gets an evergreen fallback so no lesson ever falls off the
  // video shelf, even when the topic-keyword layer misses.
  Design: {
    seedling: DESIGN_CYCLE,
    explorer: DESIGN_CYCLE,
    pioneer:  DESIGN_CYCLE,
  },
  Arabic: {
    seedling: ARABIC_ALPHABET,
    explorer: ARABIC_ALPHABET,
    pioneer:  ARABIC_ALPHABET,
  },
  "Islamic Studies": {
    seedling: ISLAMIC_STUDIES_KIDS,
    explorer: ISLAMIC_STUDIES_KIDS,
    pioneer:  ISLAMIC_STUDIES_KIDS,
  },
  Humanities: {
    seedling: COMMUNITY_HELPERS,
    explorer: GEO_CONTINENTS,
    pioneer:  GLOBAL_KIDS_UN,
  },
  "Social Studies": {
    seedling: COMMUNITY_HELPERS,
    explorer: GEO_CONTINENTS,
    pioneer:  GLOBAL_KIDS_UN,
  },
  "Global Perspectives": {
    seedling: GLOBAL_KIDS_UN,
    explorer: GLOBAL_KIDS_UN,
    pioneer:  GLOBAL_KIDS_UN,
  },
  "Understanding the World": {
    seedling: UTW_LIVING_THINGS,
    explorer: UTW_LIVING_THINGS,
    pioneer:  UTW_LIVING_THINGS,
  },
  "Expressive Arts": {
    seedling: ART_PRIMARY_COLOURS,
    explorer: ART_COLOUR,
    pioneer:  ART_COLOUR,
  },
  "Art & Design": {
    seedling: ART_PRIMARY_COLOURS,
    explorer: ART_COLOUR,
    pioneer:  ART_COLOUR,
  },
  "Visual Arts": {
    seedling: ART_PRIMARY_COLOURS,
    explorer: ART_COLOUR,
    pioneer:  ART_COLOUR,
  },
  Computing: {
    seedling: ICT_CODING,
    explorer: ICT_CODING,
    pioneer:  ICT_CODING,
  },
  "Physical Education": {
    seedling: PE_BRAIN_BREAKS,
    explorer: PE_MOVEMENT_SKILLS,
    pioneer:  PE_KIDS_EXERCISE,
  },
  "Religious Education": {
    seedling: RE_WORLD_RELIGIONS,
    explorer: RE_WORLD_RELIGIONS,
    pioneer:  RE_WORLD_RELIGIONS,
  },
  "Modern Languages": {
    seedling: LANG_HELLO,
    explorer: LANG_HELLO,
    pioneer:  LANG_HELLO,
  },
  Language: {
    seedling: LANG_HELLO,
    explorer: ENG_PHONICS,
    pioneer:  ENG_ALPHABET,
  },
  Literacy: {
    seedling: ENG_PHONICS,
    explorer: ENG_ALPHABET,
    pioneer:  ENG_ALPHABET,
  },
  Lit: {
    seedling: ENG_PHONICS,
    explorer: ENG_ALPHABET,
    pioneer:  ENG_ALPHABET,
  },
};

/**
 * Look up the best kid-safe video for a lesson.
 * Order of preference:
 *   1. Exact override for this lesson.code
 *   2. Topic-keyword match (case-insensitive substring on `topic`)
 *   3. Subject × tier pool video
 *   4. null → renderer shows a friendly "No video yet" tile.
 */
export function videoForLesson({ code, subject, ageTier, topic }) {
  if (code && LESSON_VIDEO_OVERRIDES[code]) return LESSON_VIDEO_OVERRIDES[code];
  if (topic) {
    const needle = String(topic).toLowerCase();
    for (const [kw, vid] of TOPIC_KEYWORD_VIDEOS) {
      if (needle.includes(kw)) return vid;
    }
  }
  const bySubject = SUBJECT_VIDEOS[subject];
  if (!bySubject) return null;
  return bySubject[ageTier] || bySubject.explorer || null;
}

/**
 * youtube-nocookie embed URL with kid-safety flags:
 *   • rel=0            — no related-videos overlay
 *   • modestbranding=1 — smaller YouTube logo
 *   • fs=0             — no fullscreen button (keeps kids in-app)
 *   • playsinline=1    — mobile inline playback
 *   • disablekb=1      — no keyboard shortcuts
 *   • iv_load_policy=3 — no video annotations
 *   • cc_load_policy=1 — captions on where available
 */
export function embedUrlFor(videoId) {
  const p = new URLSearchParams({
    rel: "0", modestbranding: "1", fs: "0", playsinline: "1",
    disablekb: "1", iv_load_policy: "3", cc_load_policy: "1",
  });
  return `https://www.youtube-nocookie.com/embed/${videoId}?${p.toString()}`;
}
